import { fromUnixTime, isSameYear, differenceInSeconds } from 'date-fns';

// vue-i18n/account locale codes use underscores (pt_BR, zh_CN); Intl/BCP-47 wants hyphens.
const toBcp47 = locale => locale?.replace(/_/g, '-');

const dtf = (locale, options) =>
  new Intl.DateTimeFormat(toBcp47(locale), options);

/**
 * Any Date with custom Intl options, in the given locale. For labels that need a
 * specific shape (month name, day + month) that the helpers below don't cover.
 */
export const formatDateWith = (date, locale, options) =>
  dtf(locale, options).format(date);

/**
 * Time only, locale-correct clock (12h AM/PM for en, 24h for pt-BR, etc).
 * Replaces messageStamp().
 */
export const formatMessageStamp = (time, locale) =>
  dtf(locale, { hour: 'numeric', minute: '2-digit' }).format(
    fromUnixTime(time)
  );

/**
 * Date only; switches to date + time when the timestamp isn't in the current year.
 * Replaces messageTimestamp().
 */
export const formatMessageTimestamp = (time, locale) => {
  const date = fromUnixTime(time);
  const base = { day: 'numeric', month: 'short', year: 'numeric' };
  if (isSameYear(date, new Date())) {
    return dtf(locale, base).format(date);
  }
  return dtf(locale, { ...base, hour: 'numeric', minute: '2-digit' }).format(
    date
  );
};

/**
 * Date and time together, always. Replaces call sites that passed a custom
 * date-fns format string (e.g. 'LLL d, h:mm a') to messageTimestamp()/messageStamp().
 */
export const formatDateTime = (time, locale) =>
  dtf(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(fromUnixTime(time));

/**
 * Date only, no time. Replaces dateFormat() / formatUnixDate().
 */
export const formatDateOnly = (time, locale) =>
  dtf(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(
    fromUnixTime(time)
  );

/**
 * Numeric date, field order resolved per locale (26/10/2026 for pt-BR, 10/26/2026 for en).
 */
export const formatNumericDate = (time, locale) =>
  dtf(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(
    fromUnixTime(time)
  );

const RELATIVE_UNIT_THRESHOLDS = [
  ['year', 60 * 60 * 24 * 365],
  ['month', 60 * 60 * 24 * 30],
  ['day', 60 * 60 * 24],
  ['hour', 60 * 60],
  ['minute', 60],
];

/**
 * "2 hours ago" / "há 2 horas". Replaces dynamicTime(). Picks the unit from the
 * actual time delta rather than post-processing a formatted string, so it works
 * for any locale Intl.RelativeTimeFormat supports.
 */
export const formatRelativeTime = (time, locale) => {
  const diffSeconds = differenceInSeconds(fromUnixTime(time), new Date());
  const absSeconds = Math.abs(diffSeconds);
  const rtf = new Intl.RelativeTimeFormat(toBcp47(locale), { numeric: 'auto' });
  const match = RELATIVE_UNIT_THRESHOLDS.find(
    ([, secondsInUnit]) => absSeconds >= secondsInUnit
  );
  if (!match) return rtf.format(0, 'second');
  const [unit, secondsInUnit] = match;
  return rtf.format(Math.round(diffSeconds / secondsInUnit), unit);
};

const SHORT_RELATIVE_UNITS = [
  [60 * 60 * 24 * 365, 'y'],
  [60 * 60 * 24 * 30, 'mo'],
  [60 * 60 * 24, 'd'],
  [60 * 60, 'h'],
  [60, 'm'],
];

/**
 * "2h", "3d", "now". Replaces shortTimestamp(). Abbreviations are intentionally
 * generic/untranslated (same convention as WhatsApp/Instagram) and computed
 * directly from the time delta, not by pattern-matching formatRelativeTime()'s
 * output text (which breaks for any non-English locale).
 */
export const formatShortRelativeTime = (time, { withAgo = false } = {}) => {
  const absSeconds = Math.abs(
    differenceInSeconds(new Date(), fromUnixTime(time))
  );
  if (absSeconds < 60) return 'now';

  const suffix = withAgo ? ' ago' : '';
  const [secondsInUnit, label] = SHORT_RELATIVE_UNITS.find(
    ([s]) => absSeconds >= s
  );
  return `${Math.floor(absSeconds / secondsInUnit)}${label}${suffix}`;
};
